import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Loader2, Check } from "lucide-react";
import Section from "@/components/Section";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPLICATIONS = [
  "Protein Powder",
  "Hydration / Electrolytes",
  "Functional Beverage",
  "Nootropics / Adaptogens",
  "Other",
];

const initial = {
  name: "",
  company: "",
  email: "",
  application: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.company.trim()) e.company = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.application) e.application = "Select an application";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Please provide at least 10 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/sample-requests`, form);
      setDone(true);
      setForm(initial);
      toast.success("Request submitted. Our team will be in touch shortly.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <section className="pt-32 md:pt-40 pb-12 border-b border-gray-100" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <span className="pm-overline">Contact · Sample Request</span>
          <h1 className="mt-5 font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 leading-[1.04] max-w-3xl">
            Tell us about your <span className="text-brand">formulation</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-gray-600 leading-relaxed">
            Share your application, key actives and the taste challenge you're
            working on. We'll review and propose a modulation approach,
            typically with samples within days.
          </p>
        </div>
      </section>

      <Section testId="contact-form-section" bg="white" containerClassName="!py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: info */}
          <aside className="lg:col-span-4 space-y-10">
            <div>
              <span className="pm-overline">Direct</span>
              <ul className="mt-5 space-y-5">
                <li className="flex items-start gap-3" data-testid="contact-email">
                  <Mail size={18} className="text-brand mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-gray-400 mb-0.5">Email</div>
                    <a
                      href="mailto:info@premiermodulations.com"
                      className="text-sm text-gray-900 pm-link"
                    >
                      info@premiermodulations.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-testid="contact-phone">
                  <Phone size={18} className="text-brand mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-gray-400 mb-0.5">Phone</div>
                    <a
                      href="tel:+16144078981"
                      className="text-sm text-gray-900 pm-link"
                    >
                      (614) 407-8981
                    </a>
                  </div>
                </li>
              </ul>
            </div>

            <div className="border-t border-gray-100 pt-8">
              <span className="pm-overline">What Happens Next</span>
              <ol className="mt-5 space-y-4 text-sm text-gray-600">
                {[
                  "We review your application and formulation context.",
                  "A technical team member replies within 1 to 2 business days.",
                  "If a fit, we ship samples and a technical brief.",
                ].map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="pm-num text-xs text-brand pt-0.5 w-6">
                      0{i + 1}
                    </span>
                    <span className="leading-relaxed">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* Right: form */}
          <div className="lg:col-span-8">
            {done ? (
              <div
                data-testid="contact-success-state"
                className="border border-gray-200 rounded-sm p-10 md:p-14 bg-[#F9FAFB] text-center"
              >
                <div className="mx-auto w-14 h-14 rounded-full bg-brand text-white flex items-center justify-center">
                  <Check size={22} />
                </div>
                <h2 className="mt-6 font-heading text-2xl md:text-3xl font-semibold text-gray-900">
                  Request received.
                </h2>
                <p className="mt-4 text-gray-600 max-w-md mx-auto leading-relaxed">
                  Thank you. Our technical team has received your request and
                  will be in touch within 1 to 2 business days.
                </p>
                <Button
                  onClick={() => setDone(false)}
                  data-testid="contact-submit-another"
                  className="mt-8 bg-brand hover:bg-brand-hover text-white rounded-sm h-11 px-6"
                >
                  Submit another request
                </Button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="sample-request-form"
                className="border border-gray-200 rounded-sm p-8 md:p-10 bg-white"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FieldGroup label="Name" id="name" error={errors.name}>
                    <Input
                      id="name"
                      data-testid="form-input-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Your full name"
                      className="h-11 rounded-sm bg-gray-50 border-gray-200 focus-visible:ring-brand focus-visible:border-brand"
                    />
                  </FieldGroup>
                  <FieldGroup label="Company" id="company" error={errors.company}>
                    <Input
                      id="company"
                      data-testid="form-input-company"
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      placeholder="Company name"
                      className="h-11 rounded-sm bg-gray-50 border-gray-200 focus-visible:ring-brand focus-visible:border-brand"
                    />
                  </FieldGroup>
                  <FieldGroup label="Work Email" id="email" error={errors.email}>
                    <Input
                      id="email"
                      data-testid="form-input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="name@company.com"
                      className="h-11 rounded-sm bg-gray-50 border-gray-200 focus-visible:ring-brand focus-visible:border-brand"
                    />
                  </FieldGroup>
                  <FieldGroup label="Application" id="application" error={errors.application}>
                    <Select
                      value={form.application}
                      onValueChange={(v) => update("application", v)}
                    >
                      <SelectTrigger
                        id="application"
                        data-testid="form-select-application"
                        className="h-11 rounded-sm bg-gray-50 border-gray-200 focus:ring-brand"
                      >
                        <SelectValue placeholder="Select application" />
                      </SelectTrigger>
                      <SelectContent>
                        {APPLICATIONS.map((a) => (
                          <SelectItem
                            key={a}
                            value={a}
                            data-testid={`form-select-option-${a.toLowerCase().replace(/[^a-z]/g, "-")}`}
                          >
                            {a}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                </div>

                <div className="mt-6">
                  <FieldGroup label="Message" id="message" error={errors.message}>
                    <Textarea
                      id="message"
                      data-testid="form-input-message"
                      rows={6}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Describe your formulation, actives, taste challenge, and target timeline."
                      className="rounded-sm bg-gray-50 border-gray-200 focus-visible:ring-brand focus-visible:border-brand"
                    />
                  </FieldGroup>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-gray-100 pt-6">
                  <p className="text-xs text-gray-500 max-w-sm leading-relaxed">
                    By submitting, you agree to be contacted by our technical
                    team regarding your formulation inquiry.
                  </p>
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="form-submit-button"
                    className="bg-brand hover:bg-brand-hover text-white rounded-sm h-12 px-7 text-[15px] font-medium disabled:opacity-70"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={16} />
                        Submitting…
                      </>
                    ) : (
                      "Submit Request"
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}

function FieldGroup({ label, id, error, children }) {
  return (
    <div>
      <Label
        htmlFor={id}
        className="text-[11px] tracking-[0.16em] uppercase font-medium text-gray-700 mb-2 block"
      >
        {label}
      </Label>
      {children}
      {error && (
        <p
          className="mt-1.5 text-xs text-red-600"
          data-testid={`form-error-${id}`}
        >
          {error}
        </p>
      )}
    </div>
  );
}
